import { useState, useEffect, useCallback } from "react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { ClipboardList, Loader2, Check, X, Upload, FileSpreadsheet } from "lucide-react";

const CATEGORIES = ["", "Dry Goods", "Dairy", "Produce", "Meat & Seafood", "Beverages", "Coffee", "Bakery", "Packaging", "Cleaning", "Consumables"];

export default function CountSession({ locations, isDirector, onDone, onCancel }) {
  const [phase, setPhase] = useState("choose");   // choose | active | submitted
  const [location, setLocation] = useState(locations[0]?.code || "CENTRAL");
  const [date, setDate] = useState(new Date().toISOString().slice(0, 10));
  const [category, setCategory] = useState("");
  const [note, setNote] = useState("");
  const [blind, setBlind] = useState(false);
  const [session, setSession] = useState(null);
  const [busy, setBusy] = useState(false);
  const [filter, setFilter] = useState("");

  const startCount = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/staff/inventory/v2/counts", {
        location, date, category: category || null, note, blind,
      });
      setSession(data);
      setPhase("active");
      toast.success(`Count ${data.count_no} started · ${data.lines.length} items`);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not start count");
    } finally { setBusy(false); }
  };

  const persistLines = useCallback(async (linesPatch) => {
    if (!session) return;
    try {
      const { data } = await api.patch(`/staff/inventory/v2/counts/${session.id}`, { lines: linesPatch });
      setSession(data);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Update failed");
    }
  }, [session]);

  const updateLine = (sku, counted_qty) => {
    // Update locally first for snappy UX, then persist debounced.
    setSession(prev => {
      if (!prev) return prev;
      const isBlind = prev.blind;
      const next = {
        ...prev,
        lines: prev.lines.map(l => l.sku === sku
          ? { ...l,
              counted_qty,
              variance: (counted_qty === null || isBlind || l.system_qty === null) ? null : Number((counted_qty - l.system_qty).toFixed(4)),
              variance_value: (counted_qty === null || isBlind || l.system_qty === null) ? null : Number(((counted_qty - l.system_qty) * l.system_avg_cost).toFixed(2)),
            }
          : l),
      };
      return next;
    });
  };

  // Debounced persist
  useEffect(() => {
    if (!session || phase !== "active") return;
    const h = setTimeout(() => {
      const linesPatch = session.lines
        .filter(l => l.counted_qty !== undefined)
        .map(l => ({ sku: l.sku, counted_qty: l.counted_qty, note: l.note || "" }));
      if (linesPatch.length) persistLines(linesPatch);
    }, 800);
    return () => clearTimeout(h);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [session?.lines, phase]);

  const uploadCounts = async (file) => {
    if (!file || !session) return;
    setBusy(true);
    try {
      const formData = new FormData();
      formData.append("file", file);
      const { data } = await api.post(`/staff/inventory/v2/counts/${session.id}/upload`, formData, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setSession(data.count);
      toast.success(`Applied ${data.applied} row${data.applied === 1 ? "" : "s"}${data.unknown_skus.length ? ` · ${data.unknown_skus.length} unknown SKUs skipped` : ""}`);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Upload failed");
    } finally { setBusy(false); }
  };

  const submitCount = async () => {
    if (!session) return;
    setBusy(true);
    try {
      const { data } = await api.post(`/staff/inventory/v2/counts/${session.id}/submit`);
      if (data.posted) {
        toast.success(`Auto-posted ${data.ledger_rows} adjustment${data.ledger_rows === 1 ? "" : "s"}`);
        onDone();
      } else {
        toast.warning(`Sent to Director inbox — variance over ₹${session.adjustment_cap?.toLocaleString("en-IN") || "0"} on ${data.count.over_limit_lines} line(s)`);
        setSession(data.count);
        setPhase("submitted");
      }
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Submit failed");
    } finally { setBusy(false); }
  };

  const downloadTemplate = () => {
    const url = `${process.env.REACT_APP_BACKEND_URL}/api/staff/inventory/v2/counts/template.xlsx`;
    window.open(url, "_blank");
  };

  // ---------- Views ----------

  if (phase === "choose") {
    return (
      <div data-testid="count-start" className="space-y-4 max-w-md">
        <div className="p-4 border border-[hsl(var(--rule))] bg-[hsl(var(--bg-2))]">
          <div className="flex items-start gap-3">
            <ClipboardList size={20} className="text-[hsl(var(--brass-2))] mt-0.5" />
            <div className="text-xs text-[hsl(var(--ink-soft))]">
              <b>Stock count.</b> Snapshot the system stock at a location, enter counted qty per item on the shop floor, then submit. Small variances auto-post; large ones go to the Director inbox.
            </div>
          </div>
        </div>
        <label className="block text-xs uppercase tracking-wider text-[hsl(var(--ink-soft))]">
          Location
          <select data-testid="count-location" value={location} onChange={e => setLocation(e.target.value)} className="tbb-inp mt-1 w-full">
            {locations.map(l => <option key={l.code} value={l.code}>{l.name}</option>)}
          </select>
        </label>
        <label className="block text-xs uppercase tracking-wider text-[hsl(var(--ink-soft))]">
          Date
          <input type="date" data-testid="count-date" value={date} onChange={e => setDate(e.target.value)} className="tbb-inp mt-1 w-full" />
        </label>
        <label className="block text-xs uppercase tracking-wider text-[hsl(var(--ink-soft))]">
          Category filter (optional)
          <select data-testid="count-category" value={category} onChange={e => setCategory(e.target.value)} className="tbb-inp mt-1 w-full">
            {CATEGORIES.map(c => <option key={c} value={c}>{c || "All categories"}</option>)}
          </select>
        </label>
        <label className="block text-xs uppercase tracking-wider text-[hsl(var(--ink-soft))]">
          Note
          <input value={note} onChange={e => setNote(e.target.value)} placeholder="e.g. Monthly count — main store" className="tbb-inp mt-1 w-full" />
        </label>
        <label className="flex items-start gap-2 p-3 border border-[hsl(var(--rule))] bg-[hsl(var(--bg-2))] cursor-pointer">
          <input type="checkbox" data-testid="count-blind" checked={blind} onChange={e => setBlind(e.target.checked)} className="mt-1" />
          <span className="text-xs">
            <b className="text-[hsl(var(--ink))]">Blind count</b>
            <span className="block text-[hsl(var(--ink-soft))] mt-0.5">Hide system stock while counting so the counter can't anchor to it. Variance is revealed after submit.</span>
          </span>
        </label>
        <div className="flex gap-2">
          <button data-testid="count-start-btn" onClick={startCount} disabled={busy} className="btn-brass !py-2.5 !px-5 text-sm disabled:opacity-50">
            {busy ? <Loader2 size={14} className="animate-spin inline" /> : "Start count"}
          </button>
          <button onClick={onCancel} className="text-sm px-4 py-2 text-[hsl(var(--ink-soft))]">Cancel</button>
        </div>
        <style>{`.tbb-inp{background:hsl(var(--bg));border:1px solid hsl(var(--rule));padding:0.55rem 0.75rem;font-size:0.9rem;width:100%}.tbb-inp:focus{outline:1px solid hsl(var(--forest));border-color:hsl(var(--forest))}`}</style>
      </div>
    );
  }

  if (phase === "submitted" && session) {
    return (
      <div data-testid="count-submitted" className="space-y-4">
        <div className="p-4 border-2 border-[hsl(35_75%_38%)] bg-[hsl(38_90%_96%)]">
          <div className="font-mono text-sm text-[hsl(var(--brass-2))]">{session.count_no}</div>
          <div className="font-semibold mt-1">Submitted for Director approval</div>
          <div className="text-xs text-[hsl(var(--ink-soft))] mt-1">
            {session.over_limit_lines} line{session.over_limit_lines === 1 ? "" : "s"} over ₹{session.adjustment_cap?.toLocaleString("en-IN")} · total variance ₹{session.total_variance_value_abs?.toLocaleString("en-IN")}
          </div>
        </div>
        <button onClick={onDone} className="btn-brass !py-2.5 !px-5 text-sm">Back to Inventory</button>
        <style>{`.tbb-inp{background:hsl(var(--bg));border:1px solid hsl(var(--rule));padding:0.55rem 0.75rem;font-size:0.9rem}.tbb-inp:focus{outline:1px solid hsl(var(--forest));border-color:hsl(var(--forest))}`}</style>
      </div>
    );
  }

  // active phase — line entry grid
  if (!session) return null;
  const filtered = session.lines.filter(l =>
    !filter || l.sku.toLowerCase().includes(filter.toLowerCase()) || (l.item_name || "").toLowerCase().includes(filter.toLowerCase())
  );
  const countedCount = session.lines.filter(l => l.counted_qty !== null && l.counted_qty !== undefined).length;
  const varianceTotal = session.total_variance_value_abs || 0;

  return (
    <div data-testid="count-active" className="space-y-3">
      <div className="p-3 border border-[hsl(var(--rule))] bg-[hsl(var(--bg-2))] flex items-center justify-between">
        <div>
          <div className="font-mono text-xs text-[hsl(var(--brass-2))]">{session.count_no}</div>
          <div className="text-sm">{location} · {date}{category && ` · ${category}`}</div>
          <div className="text-xs text-[hsl(var(--ink-soft))]">{countedCount}/{session.lines.length} counted · abs variance ₹{varianceTotal.toLocaleString("en-IN", { maximumFractionDigits: 0 })}</div>
        </div>
        <div className="flex gap-2">
          <button data-testid="count-template" onClick={downloadTemplate} className="text-xs px-3 py-1.5 border border-[hsl(var(--rule))] text-[hsl(var(--ink-soft))] flex items-center gap-1">
            <FileSpreadsheet size={12} /> Template
          </button>
          <label className="text-xs px-3 py-1.5 border border-[hsl(var(--forest))] text-[hsl(var(--forest))] flex items-center gap-1 cursor-pointer">
            <Upload size={12} /> Upload
            <input data-testid="count-upload" type="file" accept=".xlsx,.csv" onChange={e => uploadCounts(e.target.files?.[0])} className="hidden" />
          </label>
        </div>
      </div>

      <input value={filter} onChange={e => setFilter(e.target.value)} placeholder="Filter by SKU or name…" className="tbb-inp w-full text-sm" />

      <div className="border border-[hsl(var(--rule))] divide-y divide-[hsl(var(--rule))] max-h-[60vh] overflow-y-auto">
        {filtered.length === 0 && <div className="p-6 text-center text-[hsl(var(--ink-soft))] text-sm">No items match.</div>}
        {filtered.map(l => {
          const overCap = l.variance_value !== null && Math.abs(l.variance_value) > (session.adjustment_cap || 0);
          return (
            <div key={l.sku} data-testid={`count-row-${l.sku}`} className={`p-3 grid grid-cols-[1fr_auto] gap-3 items-center ${overCap ? "bg-[hsl(38_90%_96%)]" : ""}`}>
              <div className="min-w-0">
                <div className="text-sm truncate">{l.item_name}</div>
                <div className="text-xs text-[hsl(var(--ink-soft))]">
                  {l.sku}
                  {session.blind
                    ? ` · ${l.base_uom} · system hidden`
                    : ` · system ${l.system_qty} ${l.base_uom} @ ₹${l.system_avg_cost?.toLocaleString("en-IN", { maximumFractionDigits: 2 }) || 0}`}
                </div>
                {!session.blind && l.variance !== null && l.variance !== undefined && (
                  <div className={`text-xs mt-0.5 ${overCap ? "text-[hsl(35_75%_38%)] font-semibold" : l.variance === 0 ? "text-[hsl(var(--ink-soft))]" : l.variance < 0 ? "text-[hsl(6_62%_45%)]" : "text-[hsl(var(--forest))]"}`}>
                    Δ {l.variance > 0 ? "+" : ""}{l.variance} → ₹{l.variance_value?.toLocaleString("en-IN", { maximumFractionDigits: 2 })}
                    {overCap && " · over cap"}
                  </div>
                )}
              </div>
              <input
                type="number"
                step="0.01"
                min="0"
                inputMode="decimal"
                data-testid={`count-qty-${l.sku}`}
                value={l.counted_qty ?? ""}
                onChange={e => {
                  const v = e.target.value === "" ? null : Number(e.target.value);
                  updateLine(l.sku, v);
                }}
                placeholder="Count"
                className="tbb-inp w-24 text-right"
              />
            </div>
          );
        })}
      </div>

      <div className="flex gap-2 sticky bottom-0 bg-[hsl(var(--bg))] py-2 -mx-4 px-4 border-t border-[hsl(var(--rule))]">
        <button
          data-testid="count-submit"
          onClick={submitCount}
          disabled={busy || countedCount === 0}
          className="flex-1 btn-brass !py-3 text-sm disabled:opacity-50"
        >
          {busy ? <Loader2 size={16} className="animate-spin inline" /> : <><Check size={14} className="inline mr-1" /> Submit count ({countedCount} line{countedCount === 1 ? "" : "s"})</>}
        </button>
        <button onClick={onCancel} className="text-sm px-4 text-[hsl(var(--ink-soft))]">
          <X size={16} className="inline" /> Cancel
        </button>
      </div>
      <style>{`.tbb-inp{background:hsl(var(--bg));border:1px solid hsl(var(--rule));padding:0.55rem 0.75rem;font-size:0.9rem}.tbb-inp:focus{outline:1px solid hsl(var(--forest));border-color:hsl(var(--forest))}`}</style>
    </div>
  );
}
